/**
 * Phone number validation and normalization utilities
 */

/**
 * Validates if a string contains only digits
 * @param str String to check
 * @returns boolean indicating if string contains only digits
 */
const containsOnlyDigits = (str: string): boolean => /^\d+$/.test(str);

/**
 * Strips all non-digit characters from a phone number, preserving the + for country code
 * @param phone Phone number to clean
 * @returns string containing only digits and possibly leading +
 */
export const stripNonDigits = (phone: string): string => {
  // Preserve the + at the start if it exists
  if (phone.startsWith("+")) {
    return "+" + phone.slice(1).replace(/\D/g, "");
  }
  return phone.replace(/\D/g, "");
};

/**
 * Validates basic phone number format
 * Must be in E.164 format (+[country code][10 digits])
 * @param phone The phone number to validate
 * @returns boolean indicating if phone format is valid
 */
export const isValidPhone = (phone: string): boolean => {
  if (!phone) return true; // Empty is valid initially

  // Must start with + followed by at least one digit
  if (!/^\+\d/.test(phone)) return false;

  const digits = stripNonDigits(phone);
  const numberWithoutPlus = digits.slice(1); // Remove the +

  // Get just the local number part (after country code)
  const countryCodeLength = numberWithoutPlus.length - 10;
  if (countryCodeLength < 1) return false; // Must have at least 1 digit country code

  const localNumber = numberWithoutPlus.slice(countryCodeLength);
  return localNumber.length === 10; // Must have exactly 10 digits for local number
};

/**
 * Converts a phone number to E.164 format
 * If no country code is provided in the number, assumes US/Canada (+1)
 * @param phone The phone number to format
 * @returns E.164 formatted phone number or null if invalid
 */
export const toE164Format = (phone: string): string | null => {
  let formatted = stripNonDigits(phone);

  // If already in E.164 format (starts with +), just clean it
  if (phone.startsWith("+")) {
    return formatted;
  }

  // If 10 digits (US/Canada format), add +1
  if (formatted.length === 10) {
    return `+1${formatted}`;
  }

  // If 11 digits and starts with 1, assume US/Canada
  if (formatted.length === 11 && formatted.startsWith("1")) {
    return `+${formatted}`;
  }

  // If other length, number might be invalid or from another country
  return null;
};

/**
 * Validates and normalizes a phone number to E.164 format
 * @param phone The phone number to validate and normalize
 * @returns object containing validation result and normalized phone
 */
export const validateAndNormalizePhone = (
  phone: string
): {
  isValid: boolean;
  normalizedPhone: string | null;
} => {
  // If empty, it's initially valid but has no normalized form
  if (!phone) {
    return {
      isValid: true,
      normalizedPhone: null,
    };
  }

  // First try to convert to E.164 format
  const e164Phone = toE164Format(phone);

  // If we got a valid E.164 number, validate it
  if (e164Phone) {
    const isValid = isValidPhone(e164Phone);
    return {
      isValid,
      normalizedPhone: isValid ? e164Phone : null,
    };
  }

  // If we couldn't convert to E.164, it's invalid
  return {
    isValid: false,
    normalizedPhone: null,
  };
};
